// Controlador principal: estado del proyecto, historial (deshacer/rehacer), selección,
// sincronización con la escena y la simulación, guardado y solo lectura. Dónde se guarda lo decide el modo del
// editor (client/ui/modes.js): proyectos personales, trabajo de una tarea, plantilla o revisión.
import { getDef, PARTS, resolveProps } from './parts/index.js';
import { boardIdOf } from './parts/board-id.js';
import { computeLayout, simProject, partProps, overBreadboard, rotXZ } from './connectivity.js';
import { Stage } from './scene/stage.js';
import { World } from './scene/world.js';
import { Interaction } from './scene/interaction.js';
import { SimController } from './sim-controller.js';
import { api } from './api.js';
import { EXAMPLES, DEFAULT_EXAMPLE } from './examples/index.js';
import { WIRE_COLORS } from './scene/wires.js';
import { BLANK_CODE, PREFIX, blankProject } from './blank.js';

export { BLANK_CODE };

function clone(o) {
  return JSON.parse(JSON.stringify(o));
}

function stripProject(p) {
  return {
    name: p.name,
    files: p.files,
    parts: p.parts.filter((x) => !x._ghost).map(({ id, type, x, z, rot, props }) => ({ id, type, x: round(x), z: round(z), rot: rot || 0, props: props || {} })),
    wires: p.wires.map(({ id, a, b, color }) => ({ id, a, b, color })),
  };
}

function round(v) {
  return Math.round(v * 1000) / 1000;
}

export class App {
  constructor() {
    this.listeners = new Map();
    this.project = null;
    this.projectId = null;
    this.selection = null;
    this.undoStack = [];
    this.redoStack = [];
    this.pendingSnapshot = null;
    this.dirty = false;
    this.saving = false;
    this.wireColor = 'green';
    this.locked = false; // no se pueden mover piezas (lo sigue a readOnly)
    this.ui = {};
    // Cuenta y modo del editor (los fija client/ui/modes.js):
    this.user = null; // cuenta con sesión (con su objeto Plan en user.plan), o null
    this.kind = 'project'; // 'project' (/ y /p/ propio), 'foreign' (/p/ ajeno), 'work', 'template' o 'review'
    this.visibility = 'link'; // del proyecto propio guardado: 'link' (cualquiera con el enlace) o 'private' (solo yo)
    this.readOnly = ''; // motivo por el que no se puede editar ('' = se puede)
    this.saveHandler = null; // guardado de una tarea: async (proyecto, { keepalive }) => { updatedAt }
    this.draftKey = null; // borrador local de una tarea: arduino3d.draft.<clave>
    this.copyName = null; // nombre para «Guardar una copia» en las tareas
    this.owner = null; // dueño de un proyecto ajeno: { name }
    this.saveError = '';
    this.leaving = false; // se sale a propósito (p. ej. a entrar) con el borrador local a salvo
  }

  on(ev, fn) {
    if (!this.listeners.has(ev)) this.listeners.set(ev, new Set());
    this.listeners.get(ev).add(fn);
  }
  emit(ev, data) {
    for (const fn of this.listeners.get(ev) || []) fn(data);
  }

  init(container) {
    this.stage = new Stage(container);
    this.world = new World(this.stage);
    this.interaction = new Interaction(this);
    this.sim = new SimController(this);
    window.addEventListener('keydown', (e) => this.onKey(e));
    window.addEventListener('keyup', (e) => this.onKeyUp(e));
    window.addEventListener('beforeunload', (e) => {
      if (!this.dirty || this.readOnly || this.kind === 'review') return;
      this.writeDraft();
      // Último intento de guardar: con keepalive la petición sigue aunque se cierre la página (hasta 64 KB);
      // si no alcanza, el borrador local se recupera al volver.
      if (this.projectId || this.saveHandler) this.saveNow({ keepalive: true });
      else if (this.hasUserChanges && !this.leaving) e.preventDefault();
    });
  }

  // Proyecto -----------------------------------------------------------------------------------
  load(project, { id = null, keepView = false, url = null } = {}) {
    if (this.sim.active) this.sim.stop();
    this.sim.forgetEeprom();
    this.project = {
      name: project.name || 'Proyecto sin título',
      files: { ...(project.files || { 'sketch.ino': project.code || BLANK_CODE }) },
      parts: clone(project.parts || []),
      wires: clone(project.wires || []),
    };
    this.projectId = id;
    this.undoStack = [];
    this.redoStack = [];
    this.selection = null;
    this.dirty = false;
    this.hasUserChanges = false;
    this.refresh();
    this.emit('project-loaded', this.project);
    this.emit('selection', null);
    this.emit('save-state');
    if (!keepView) setTimeout(() => this.fitView(), 30);
    // Las tareas conservan su ruta (/tareas/…, /entregas/…); los proyectos van en /p/<id> o en /.
    const path = url || (id ? `/p/${id}` : '/');
    if (location.pathname !== path) history.replaceState(null, '', path);
  }

  /** Recuerda el último proyecto propio para abrirlo al volver a /. */
  remember(id) {
    try {
      localStorage.setItem('arduino3d.last', id);
    } catch {
      // sin almacenamiento local
    }
  }

  setUser(user) {
    this.user = user || null;
    this.emit('user', this.user);
  }

  /** Objeto Plan de la cuenta (docs/PLANES.md), o null sin sesión. */
  get plan() {
    return (this.user && this.user.plan) || null;
  }

  /**
   * Vuelve a leer el plan (GET /api/plan): pudo cambiar desde que se abrió la página (lo asignó el administrador, venció
   * o se entró a un aula con plan Aulas). Devuelve el plan con su uso (`usage`).
   */
  async refreshPlan() {
    const { usage, ...plan } = await api.get('/api/plan');
    if (this.user) this.user.plan = plan;
    return { ...plan, usage };
  }

  /** Vuelve al modo de siempre (proyecto propio o sin guardar) antes de cargar otro proyecto. */
  resetMode() {
    this.kind = 'project';
    this.visibility = 'link';
    this.owner = null;
    this.saveHandler = null;
    this.draftKey = null;
    this.copyName = null;
    this.saveError = '';
    if (this.readOnly) this.setReadOnly('');
    this.emit('mode');
  }

  /** Solo lectura (con el motivo que se muestra al intentar editar) o edición normal (''). */
  setReadOnly(reason) {
    this.readOnly = reason || '';
    this.locked = !!this.readOnly;
    if (this.readOnly) {
      clearTimeout(this.saveTimer);
      if (this.interaction && this.interaction.mode !== 'idle') this.interaction.cancel();
    }
    this.emit('read-only', this.readOnly);
    this.emit('save-state');
  }

  /** En solo lectura avisa por qué no se puede editar y devuelve true. */
  blocked() {
    if (!this.readOnly) return false;
    const now = Date.now();
    if (now - (this.blockedAt || 0) > 2500) {
      this.blockedAt = now;
      this.toast(this.readOnly);
    }
    return true;
  }

  refresh({ simUpdate = true } = {}) {
    this.layout = computeLayout(this.project);
    this.world.sync(this.project, this.layout);
    this.interaction.refreshSelection();
    if (simUpdate && this.sim.active) this.sim.projectChanged();
    this.emit('layout', this.layout);
  }

  simProject() {
    const p = { ...this.project, parts: this.project.parts.filter((x) => !x._ghost) };
    return simProject(p, computeLayout(p));
  }

  boardType() {
    const b = this.project.parts.find((p) => getDef(p.type).kind === 'board');
    return b ? b.type : null;
  }

  /** Placa con las opciones que cambian el programa compilado y el reloj (versión de la Pro Mini, reloj del ATtiny85). */
  boardKey() {
    const b = this.project.parts.find((p) => getDef(p.type).kind === 'board');
    return b ? boardIdOf(b) || b.type : null;
  }

  snapshot() {
    return JSON.stringify(stripProject(this.project));
  }

  /** Guarda el estado actual para poder deshacer el cambio que viene. */
  beginChange() {
    if (!this.pendingSnapshot) this.pendingSnapshot = this.snapshot();
  }

  endChange(label = 'Cambio') {
    const before = this.pendingSnapshot;
    this.pendingSnapshot = null;
    if (!before) return;
    const after = this.snapshot();
    if (before === after) return;
    this.undoStack.push({ label, state: before });
    if (this.undoStack.length > 150) this.undoStack.shift();
    this.redoStack = [];
    this.markDirty();
  }

  change(label, fn) {
    this.beginChange();
    fn();
    this.refresh();
    this.endChange(label);
  }

  applyState(json) {
    const s = JSON.parse(json);
    this.project.parts = s.parts;
    this.project.wires = s.wires;
    if (this.selection && !this.findSelection()) this.select(null);
    this.refresh();
    this.markDirty();
    this.emit('selection', this.selection);
  }

  undo() {
    if (this.blocked()) return;
    const item = this.undoStack.pop();
    if (!item) return;
    this.redoStack.push({ label: item.label, state: this.snapshot() });
    this.applyState(item.state);
    this.toast(`Deshecho: ${item.label}`);
  }

  redo() {
    if (this.blocked()) return;
    const item = this.redoStack.pop();
    if (!item) return;
    this.undoStack.push({ label: item.label, state: this.snapshot() });
    this.applyState(item.state);
    this.toast(`Rehecho: ${item.label}`);
  }

  markDirty() {
    // En solo lectura se puede simular y usar los controles, pero eso no es un cambio que guardar.
    if (this.readOnly) return;
    this.dirty = true;
    this.hasUserChanges = true;
    this.emit('save-state');
    this.scheduleSave();
    this.saveDraft();
  }

  // Piezas -----------------------------------------------------------------------------------------
  newId(type) {
    const prefix = PREFIX[type] || type.replace(/[^a-z]/g, '').slice(0, 6) || 'p';
    const used = new Set(this.project.parts.map((p) => p.id));
    if (!used.has(prefix) && getDef(type).kind === 'board') return prefix;
    for (let n = 1; ; n++) if (!used.has(prefix + n)) return prefix + n;
  }

  addGhost(type, props = {}) {
    const def = getDef(type);
    const defaults = {};
    for (const p of def.props) if (props[p.key] !== undefined) defaults[p.key] = props[p.key];
    const part = { id: this.newId(type), type, x: 0, z: 0, rot: 0, props: defaults, _ghost: true };
    this.project.parts.push(part);
    this.refresh({ simUpdate: false });
    return part;
  }

  moveGhost(pos) {
    const g = this.project.parts.find((p) => p._ghost);
    if (!g) return;
    g.x = pos.x;
    g.z = pos.z;
    this.refresh({ simUpdate: false });
  }

  placeGhost(part) {
    if (this.blocked()) return;
    const g = this.project.parts.find((p) => p === part);
    if (!g) return;
    if (getDef(g.type).kind === 'board' && this.project.parts.some((p) => p !== g && getDef(p.type).kind === 'board')) {
      this.removeGhost(g);
      this.toast('Solo puede haber una placa Arduino por proyecto.', true);
      return;
    }
    this.beginChange();
    delete g._ghost;
    this.refresh();
    this.endChange(`Agregar ${getDef(g.type).name}`);
    this.select({ type: 'part', id: g.id });
  }

  removeGhost(part) {
    this.project.parts = this.project.parts.filter((p) => p !== part);
    this.refresh({ simUpdate: false });
  }

  /** Piezas montadas sobre una protoboard (se mueven y giran con ella). */
  childrenOf(id) {
    const bb = this.project.parts.find((p) => p.id === id);
    if (!bb || getDef(bb.type).kind !== 'breadboard') return [];
    return this.project.parts.filter((p) => p !== bb && !p._ghost && (this.layout.inserted.get(p.id) === id || (this.layout.y.get(p.id) > 0 && overBreadboard(bb, p.x, p.z))));
  }

  movePartLive(id, x, z) {
    if (this.locked) return;
    const part = this.project.parts.find((p) => p.id === id);
    if (!part) return;
    if (!this.dragging || this.dragging.id !== id) this.dragging = { id, children: this.childrenOf(id) };
    const dx = x - part.x;
    const dz = z - part.z;
    part.x = x;
    part.z = z;
    for (const c of this.dragging.children) {
      c.x += dx;
      c.z += dz;
    }
    this.refresh();
  }

  endDrag() {
    this.dragging = null;
  }

  rotatePart(id, dir = 1) {
    if (this.blocked()) return;
    this.change('Girar pieza', () => {
      const part = this.project.parts.find((p) => p.id === id);
      if (!part) return;
      const children = this.childrenOf(id);
      part.rot = (((part.rot || 0) + 90 * dir) % 360 + 360) % 360;
      // Las piezas de encima giran alrededor del centro de la protoboard
      for (const c of children) {
        const [rx, rz] = rotXZ(c.x - part.x, c.z - part.z, 90 * dir);
        c.x = part.x + rx;
        c.z = part.z + rz;
        c.rot = (((c.rot || 0) + 90 * dir) % 360 + 360) % 360;
      }
    });
  }

  deletePart(id) {
    if (this.blocked()) return;
    const part = this.project.parts.find((p) => p.id === id);
    if (!part) return;
    this.change(`Eliminar ${getDef(part.type).name}`, () => {
      this.project.parts = this.project.parts.filter((p) => p.id !== id);
      this.project.wires = this.project.wires.filter((w) => w.a.part !== id && w.b.part !== id);
    });
    if (this.selection && this.selection.id === id) this.select(null);
  }

  duplicatePart(id) {
    if (this.blocked()) return;
    const part = this.project.parts.find((p) => p.id === id);
    if (!part || getDef(part.type).kind === 'board') return;
    let copy;
    this.change('Duplicar pieza', () => {
      copy = { ...clone(part), id: this.newId(part.type), x: part.x + 12.7, z: part.z + 12.7 };
      delete copy._resolved;
      delete copy._resolvedFrom;
      this.project.parts.push(copy);
    });
    this.select({ type: 'part', id: copy.id });
  }

  setProp(id, key, value) {
    const part = this.project.parts.find((p) => p.id === id);
    if (!part) return;
    const def = getDef(part.type);
    const pdef = def.props.find((p) => p.key === key);
    // Una prop «runtime» la atiende el modelo que ya corre: reconstruir el circuito cortaría lo que
    // esté a medias (un eco del HC-SR04, una trama DHT, una transacción 1-Wire…).
    const live = !!(pdef && pdef.runtime && this.sim.active);
    this.beginChange();
    part.props = { ...part.props, [key]: value };
    this.refresh({ simUpdate: !live });
    this.endChange('Cambiar propiedad');
    if (live) this.sim.input(id, key, value);
    this.emit('part-props', id);
  }

  /** Cambio continuo (perillas, deslizadores): sin refrescar todo ni crear historial por cada paso. */
  setLiveProp(id, key, value, commit) {
    const part = this.project.parts.find((p) => p.id === id);
    if (!part) return;
    if (commit) this.beginChange();
    part.props = { ...part.props, [key]: value };
    const view = this.world.views.get(id);
    const props = partProps(part);
    if (view && view.view.setProps) view.view.setProps(props);
    if (view) view.props = props;
    this.world.kick(id);
    this.stage.invalidate();
    if (this.sim.active) this.sim.input(id, key, value);
    this.emit('part-props', id);
    if (commit) this.endChange('Ajustar control');
    else this.hasUserChanges = true;
  }

  togglePart(id, key) {
    const part = this.project.parts.find((p) => p.id === id);
    if (!part) return;
    const def = getDef(part.type);
    const props = partProps(part);
    if (def.toggle) {
      const next = def.toggle(props, key);
      this.setProp(id, next.key, next.value);
      return;
    }
    // El control alterna su propia prop: booleana, o select (pasa a la siguiente opción).
    const pdef = def.props.find((p) => p.key === key);
    if (pdef && pdef.type === 'boolean') {
      this.setProp(id, key, !props[key]);
      return;
    }
    if (pdef && pdef.type === 'select') {
      const values = pdef.options.map((o) => o.value);
      this.setProp(id, key, values[(values.indexOf(props[key]) + 1) % values.length]);
      return;
    }
    this.setProp(id, 'position', props.position === 'right' ? 'left' : 'right');
  }

  partInput(id, key, value) {
    if (this.sim.active) this.sim.input(id, key, value);
  }

  // Cables ---------------------------------------------------------------------------------------
  wireColorFor(a, b) {
    const kinds = [a && (a.kind || (a.def && a.def.kind)), b && b.def && b.def.kind];
    if (kinds.includes('gnd')) return 'black';
    if (kinds.includes('power')) return 'red';
    return this.wireColor;
  }

  addWire(a, b, color) {
    if (this.blocked()) return;
    const dup = this.project.wires.find((w) => (same(w.a, a) && same(w.b, b)) || (same(w.a, b) && same(w.b, a)));
    if (dup) {
      this.select({ type: 'wire', id: dup.id });
      return;
    }
    const used = new Set(this.project.wires.map((w) => w.id));
    let n = 1;
    while (used.has('w' + n)) n++;
    const wire = { id: 'w' + n, a, b, color };
    this.change('Agregar cable', () => this.project.wires.push(wire));
    this.select({ type: 'wire', id: wire.id });
  }

  deleteWire(id) {
    if (this.blocked()) return;
    this.change('Eliminar cable', () => {
      this.project.wires = this.project.wires.filter((w) => w.id !== id);
    });
    if (this.selection && this.selection.id === id) this.select(null);
  }

  setWireColor(id, color) {
    if (this.blocked()) return;
    this.change('Color del cable', () => {
      const w = this.project.wires.find((x) => x.id === id);
      if (w) w.color = color;
    });
    this.emit('selection', this.selection);
  }

  // Selección ------------------------------------------------------------------------------------
  findSelection() {
    const s = this.selection;
    if (!s) return null;
    return s.type === 'part' ? this.project.parts.find((p) => p.id === s.id) : this.project.wires.find((w) => w.id === s.id);
  }

  select(sel) {
    const prev = this.selection;
    if (prev && prev.type === 'wire') this.world.setWireState(prev.id, 0);
    this.selection = sel;
    if (sel && sel.type === 'wire') this.world.setWireState(sel.id, 2);
    this.interaction.refreshSelection();
    this.emit('selection', sel);
  }

  deleteSelection() {
    const s = this.selection;
    if (!s) return;
    if (s.type === 'part') this.deletePart(s.id);
    else this.deleteWire(s.id);
  }

  focusInspector() {
    this.emit('focus-inspector');
  }

  // Vista ------------------------------------------------------------------------------------------
  fitView(top = false) {
    this.stage.frameBox(this.world.bounds(), { top });
  }

  setView(v) {
    if (v === 'fit') return this.fitView(this.stage.camera.position.y > 3 * Math.hypot(this.stage.camera.position.x - this.stage.controls.target.x, this.stage.camera.position.z - this.stage.controls.target.z));
    this.fitView(v === 'top');
  }

  setHint(html) {
    this.emit('hint', html);
  }

  showPinTip(pin, e) {
    this.emit('pin-tip', { pin, e });
  }

  toast(msg, error = false) {
    this.emit('toast', { msg, error });
  }

  // Código -------------------------------------------------------------------------------------------
  setFile(name, content) {
    if (this.project.files[name] === content || this.blocked()) return;
    this.project.files[name] = content;
    this.markDirty();
    if (this.sim.active) this.emit('code-changed', this.sim.codeChanged);
  }

  addFile(name) {
    if (this.blocked()) return false;
    if (this.project.files[name] !== undefined) return false;
    this.project.files[name] = name.endsWith('.h') || name.endsWith('.hpp') ? `#pragma once\n\n` : `#include <Arduino.h>\n\n`;
    this.markDirty();
    this.emit('files');
    return true;
  }

  removeFile(name) {
    if (this.blocked()) return;
    if (name === 'sketch.ino') return;
    delete this.project.files[name];
    this.markDirty();
    this.emit('files');
  }

  rename(name) {
    if (this.blocked()) return;
    this.project.name = name.trim() || 'Proyecto sin título';
    this.markDirty();
  }

  // Guardado -------------------------------------------------------------------------------------
  scheduleSave() {
    clearTimeout(this.saveTimer);
    if (this.readOnly || (!this.projectId && !this.saveHandler)) return;
    this.saveTimer = setTimeout(() => this.saveNow(), 1500);
  }

  /** Dónde va el borrador local: el de siempre para proyectos, uno por tarea, ninguno en revisión o solo lectura. */
  draftStorageKey() {
    if (this.readOnly || this.kind === 'review' || this.kind === 'foreign') return null;
    return this.draftKey ? 'arduino3d.draft.' + this.draftKey : 'arduino3d.draft';
  }

  saveDraft() {
    clearTimeout(this.draftTimer);
    this.draftTimer = setTimeout(() => this.writeDraft(), 600);
  }

  writeDraft() {
    clearTimeout(this.draftTimer);
    const key = this.draftStorageKey();
    if (!key || !this.project || !this.hasUserChanges) return;
    try {
      localStorage.setItem(key, JSON.stringify({ id: this.projectId, project: stripProject(this.project), at: Date.now() }));
    } catch {
      // sin almacenamiento local
    }
  }

  clearTaskDraft() {
    try {
      if (this.draftKey) localStorage.removeItem('arduino3d.draft.' + this.draftKey);
    } catch {
      // sin almacenamiento local
    }
  }

  async saveNow({ copy = false, keepalive = false } = {}) {
    clearTimeout(this.saveTimer);
    if (copy || this.kind === 'foreign') return keepalive ? undefined : this.saveCopy();
    if (this.readOnly) {
      this.blocked();
      return;
    }
    if (this.kind === 'review') {
      this.toast('En la revisión nada se guarda. Para conservar este trabajo usa Archivo → Guardar una copia.');
      return;
    }
    // Sin cuenta no se guarda en el servidor: el borrador local se conserva mientras tanto.
    if (!this.saveHandler && !this.user) {
      this.writeDraft();
      if (!keepalive) this.emit('need-login', 'save');
      return;
    }
    if (this.saving) {
      this.saveAgain = true;
      return;
    }
    this.saving = true;
    this.emit('save-state');
    const body = stripProject(this.project);
    try {
      let saved;
      if (this.saveHandler) saved = await this.saveHandler(body, { keepalive });
      else if (this.projectId) saved = await api.put(`/api/projects/${this.projectId}`, body, { keepalive });
      else {
        saved = await api.post('/api/projects', body, { keepalive });
        this.projectId = saved.id;
        this.kind = 'project';
        this.visibility = saved.visibility || 'link';
        history.replaceState(null, '', `/p/${saved.id}`);
        this.remember(saved.id);
        this.emit('project-loaded', this.project);
        this.emit('mode');
        if (!keepalive) this.toast(`Guardado en Mis proyectos: «${this.project.name}»`);
      }
      this.dirty = false;
      this.saveError = '';
      this.lastSaved = new Date(saved.updatedAt || Date.now());
      if (!this.saveAgain) this.clearTaskDraft();
    } catch (e) {
      this.saveError = e.message;
      if (keepalive) return;
      if (e.status === 401) {
        this.setUser(null);
        this.emit('need-login', 'expired');
      } else if (e.status === 409 && (this.kind === 'work' || this.kind === 'template')) this.emit('task-conflict', e);
      // Proyecto nuevo con los proyectos que guarda su plan ya ocupados (docs/PLANES.md): client/ui/plan.js lo explica.
      else if (e.status === 409 && !this.projectId && !this.saveHandler) this.emit('project-limit', e.message);
      else this.toast('No se pudo guardar: ' + e.message, true);
    } finally {
      this.saving = false;
      this.emit('save-state');
      if (this.saveAgain) {
        this.saveAgain = false;
        this.saveNow();
      }
    }
  }

  /** Guarda ya lo pendiente (antes de entregar o de salir a otra página). Devuelve false si quedó sin guardar. */
  async flushSave() {
    clearTimeout(this.saveTimer);
    const wait = async () => {
      while (this.saving) await new Promise((r) => setTimeout(r, 60));
    };
    if (this.readOnly || this.kind === 'review' || (!this.projectId && !this.saveHandler)) return true;
    await wait();
    if (this.dirty) await this.saveNow();
    await wait();
    return !this.dirty;
  }

  /** «Guardar una copia»: un proyecto nuevo en Mis proyectos con lo que se ve ahora. */
  async saveCopy() {
    if (!this.user) {
      this.writeDraft();
      this.emit('need-login', 'copy');
      return;
    }
    const body = stripProject(this.project);
    body.name = `${this.copyName || body.name} (copia)`.slice(0, 120);
    // La copia de un proyecto privado propio también es privada, si el plan lo permite (si no, va con enlace).
    if (this.kind === 'project' && this.projectId && this.visibility === 'private' && this.plan && this.plan.privateProjects) body.visibility = 'private';
    try {
      const saved = await api.post('/api/projects', body);
      if (this.kind === 'project' || this.kind === 'foreign') {
        // Se sigue trabajando en la copia, ya propia y editable.
        this.kind = 'project';
        this.visibility = saved.visibility || 'link';
        this.owner = null;
        this.setReadOnly('');
        this.projectId = saved.id;
        this.project.name = body.name;
        this.dirty = false;
        this.saveError = '';
        history.replaceState(null, '', `/p/${saved.id}`);
        this.remember(saved.id);
        this.emit('project-loaded', this.project);
        this.emit('mode');
        this.toast(`Copia guardada en Mis proyectos: «${body.name}»`);
      } else {
        // En una tarea la pantalla sigue igual: la copia queda aparte.
        this.toast(`Se guardó una copia en Mis proyectos: «${body.name}»`);
      }
    } catch (e) {
      if (e.status === 401) {
        this.setUser(null);
        this.emit('need-login', 'copy');
      } else if (e.status === 409) this.emit('project-limit', e.message);
      else {
        // 403: el plan cambió (p. ej. venció Maker) desde que se abrió la página; la próxima copia ya va con enlace.
        if (e.status === 403) this.refreshPlan().catch(() => {});
        this.toast('No se pudo guardar la copia: ' + e.message, true);
      }
    } finally {
      this.emit('save-state');
    }
  }

  /**
   * Cambia quién puede abrir el proyecto propio guardado (docs/PLANES.md): 'link' o 'private' (es de Maker). Se guarda
   * en ese momento, con lo que se ve. Lanza el error del servidor (p. ej. 403 sin Maker) para mostrarlo donde se pidió.
   */
  async setVisibility(visibility) {
    if (this.kind !== 'project' || !this.projectId) return;
    clearTimeout(this.saveTimer);
    while (this.saving) await new Promise((r) => setTimeout(r, 60));
    this.saving = true;
    this.emit('save-state');
    try {
      // Solo aquí se manda `visibility`: en los guardados de siempre el servidor deja la que tenía, así otra pestaña
      // con el proyecto abierto no deshace el cambio.
      const saved = await api.put(`/api/projects/${this.projectId}`, { ...stripProject(this.project), visibility });
      this.visibility = saved.visibility;
      this.dirty = false;
      this.saveError = '';
      this.lastSaved = new Date(saved.updatedAt || Date.now());
      this.emit('visibility', this.visibility);
    } finally {
      this.saving = false;
      this.emit('save-state');
    }
  }

  /** Texto del estado del guardado: qué se guarda y dónde. */
  saveStatus() {
    if (this.readOnly) return { text: 'Solo lectura', cls: 'ro', title: this.readOnly };
    if (this.kind === 'review') return { text: 'Revisión: no se guarda', cls: 'ro', title: 'Puedes simular y mover cosas, pero nada de lo que cambies aquí se guarda.' };
    if (this.saving) return { text: 'Guardando…', cls: '', title: '' };
    if (this.saveError && this.dirty) return { text: 'Sin guardar: reintenta', cls: 'dirty', title: 'No se pudo guardar: ' + this.saveError };
    const where = { work: 'en tu entrega', template: 'en la plantilla' }[this.kind] || 'en Mis proyectos';
    if (this.saveHandler || this.projectId) {
      return this.dirty
        ? { text: 'Cambios sin guardar', cls: 'dirty', title: `Se guardan solos ${where} en unos segundos.` }
        : { text: this.kind === 'project' ? 'Guardado' : `Guardado ${where}`, cls: 'ok', title: `Se guarda solo ${where} con cada cambio.` };
    }
    if (!this.hasUserChanges) return { text: 'Ejemplo · sin guardar', cls: '', title: 'Haz cambios y guárdalos con Ctrl+S.' };
    return this.user
      ? { text: 'Sin guardar · Ctrl+S', cls: 'dirty', title: 'Ctrl+S lo guarda en Mis proyectos. Mientras tanto queda un borrador en este navegador.' }
      : { text: 'Solo en este navegador', cls: 'dirty', title: 'Es un borrador local. Entra o crea una cuenta para guardarlo en el servidor.' };
  }

  /** Abre un proyecto guardado; link: se abrió por su enlace (/p/:id). Devuelve false si no se pudo (y lo avisa). */
  async openProject(id, { link = false } = {}) {
    let p;
    try {
      p = await api.get(`/api/projects/${id}`);
    } catch (e) {
      // Por enlace, un 404 es un proyecto que ya no existe o que es privado de alguien más (el servidor responde lo
      // mismo a los dos): client/ui/plan.js lo explica.
      if (link && e.status === 404) this.emit('project-unavailable', id);
      else this.toast('No se pudo abrir el proyecto: ' + e.message, true);
      return false;
    }
    const mine = p.mine !== false;
    this.resetMode();
    this.kind = mine ? 'project' : 'foreign';
    this.visibility = p.visibility || 'link';
    this.owner = p.owner || null;
    if (!mine) this.setReadOnly(`Solo lectura: es un proyecto de ${(p.owner && p.owner.name) || 'otra persona'}. Usa «Guardar una copia» para cambiarlo.`);
    this.load(p, { id });
    this.lastLoadedAt = Date.parse(p.updatedAt) || 0;
    if (mine) this.remember(id);
    this.emit('mode');
    return true;
  }

  openExample(id) {
    const ex = EXAMPLES.find((x) => x.id === id) || EXAMPLES.find((x) => x.id === DEFAULT_EXAMPLE);
    this.resetMode();
    this.load(clone(ex.project), { id: null });
  }

  newProject(board = 'arduino-uno') {
    this.resetMode();
    this.load(blankProject(board));
  }

  exportJson() {
    const data = JSON.stringify({ format: 'arduino3d', version: 1, ...stripProject(this.project) }, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = (this.project.name || 'proyecto').replace(/[^\w\-áéíóúñÁÉÍÓÚÑ ]+/g, '').trim().replace(/\s+/g, '-') + '.json';
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  }

  importJson(text) {
    let data;
    try {
      data = JSON.parse(text);
    } catch {
      this.toast('El archivo no es un JSON válido.', true);
      return;
    }
    if (!Array.isArray(data.parts) || !Array.isArray(data.wires)) {
      this.toast('El archivo no parece un proyecto del simulador.', true);
      return;
    }
    const unknown = data.parts.filter((p) => !PARTS[p.type]).map((p) => p.type);
    if (unknown.length) this.toast(`Se omitieron piezas desconocidas: ${[...new Set(unknown)].join(', ')}`, true);
    data.parts = data.parts.filter((p) => PARTS[p.type]);
    this.resetMode();
    this.load(data, { id: null });
    this.hasUserChanges = true;
    this.markDirty();
  }

  // Teclado --------------------------------------------------------------------------------------
  inTextField(e) {
    const t = e.target;
    return t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || (t.closest && t.closest('.cm-editor')));
  }

  onKey(e) {
    const mod = e.ctrlKey || e.metaKey;
    if (mod && e.key.toLowerCase() === 's') {
      e.preventDefault();
      this.saveNow();
      return;
    }
    if (mod && e.key === 'Enter') {
      e.preventDefault();
      this.sim.active ? this.sim.stop() : this.sim.start();
      return;
    }
    if (this.inTextField(e)) return;
    if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) {
      e.preventDefault();
      this.undo();
      return;
    }
    if (mod && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
      e.preventDefault();
      this.redo();
      return;
    }
    if (mod && e.key.toLowerCase() === 'd') {
      e.preventDefault();
      if (this.selection && this.selection.type === 'part') this.duplicatePart(this.selection.id);
      return;
    }
    if (e.key === 'Escape') {
      if (this.interaction.mode !== 'idle') this.interaction.cancel();
      else this.select(null);
      this.emit('escape');
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (this.selection) {
        e.preventDefault();
        this.deleteSelection();
      }
      return;
    }
    if (e.key.toLowerCase() === 'r' && !mod) {
      if (this.interaction.mode === 'placing') this.interaction.rotatePlacing(e.shiftKey ? -1 : 1);
      else if (this.selection && this.selection.type === 'part') this.rotatePart(this.selection.id, e.shiftKey ? -1 : 1);
      return;
    }
    this.keyToButtons(e, true);
  }

  onKeyUp(e) {
    if (this.inTextField(e)) return;
    this.keyToButtons(e, false);
  }

  /** Botones con tecla asignada (propiedad "Tecla del teclado"). */
  keyToButtons(e, down) {
    const k = e.key === ' ' ? 'espacio' : e.key.toLowerCase();
    for (const part of this.project.parts) {
      if (part.type !== 'pushbutton') continue;
      // Un espacio solo (" ", como en los ejemplos) es la barra espaciadora: con trim() quedaría vacío.
      const raw = String(partProps(part).key || '');
      const key = raw === ' ' ? 'espacio' : raw.trim().toLowerCase();
      if (!key || key !== k) continue;
      // Cancelada también al repetir y al soltar: si no, el navegador la usa en el botón con foco ("Detener").
      e.preventDefault();
      if (e.repeat) continue;
      const view = this.world.views.get(part.id);
      if (view && view.view.press) view.view.press('pressed', down);
      this.stage.invalidate();
      this.partInput(part.id, 'pressed', down);
    }
  }
}

function same(a, b) {
  return a.part === b.part && a.pin === b.pin;
}

export { WIRE_COLORS, resolveProps };
